import { ThemeProvider } from '@mui/material';
import { getCurrentUserPreferences, updateCurrentUserPreferences } from 'api/userPreferences';
import React, { createContext, FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import store from 'redux/store';
import { darkTheme, lightTheme } from './theme/theme';

type ColorModePreference = 'light' | 'dark' | 'system';

const COLOR_MODE_STORAGE_KEY = 'colorMode';

interface IColorModeContext {
  toggleColorMode: () => void;
  mode: 'light' | 'dark';
}

export const ColorModeContext = createContext<IColorModeContext>({
  toggleColorMode: () => {},
  mode: 'light',
});

interface Props {
  children?: ReactNode;
}

const normalizeColorModePreference = (value: unknown): ColorModePreference | null => {
  if (typeof value !== 'string') {
    return null;
  }

  if (value === 'light' || value === 'dark' || value === 'system') {
    return value;
  }

  if (value === 'auto') {
    return 'system';
  }

  try {
    return normalizeColorModePreference(JSON.parse(value));
  } catch {
    return null;
  }
};

const hasStoredColorModePreference = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(COLOR_MODE_STORAGE_KEY) !== null;
};

const getStoredColorModePreference = (): ColorModePreference | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return normalizeColorModePreference(window.localStorage.getItem(COLOR_MODE_STORAGE_KEY));
};

const setStoredColorModePreference = (preference: ColorModePreference) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, preference);
};

const getSystemColorMode = (): 'light' | 'dark' => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const getThemePreferenceFromUserPreferences = (preferences: Record<string, any>): ColorModePreference | null =>
  normalizeColorModePreference(preferences?.theme?.mode);

export const ColorModeContextProvider: FC<React.PropsWithChildren<Props>> = ({ children }) => {
  const [preference, setPreference] = useState<ColorModePreference>(() => getStoredColorModePreference() ?? 'light');
  const [systemMode, setSystemMode] = useState<'light' | 'dark'>(() => getSystemColorMode());
  const [authState, setAuthState] = useState(() => {
    const auth = store.getState().auth;
    return {
      accessToken: auth.accessToken,
      triedAuthenticateFromLocalStorage: auth.triedAuthenticateFromLocalStorage,
    };
  });
  const lastLoadedAccessTokenRef = useRef<string | null>(null);

  const applyPreference = (nextPreference: ColorModePreference, persistToServer = true) => {
    setPreference(nextPreference);
    setStoredColorModePreference(nextPreference);

    if (persistToServer && authState.accessToken) {
      void updateCurrentUserPreferences({
        theme: {
          mode: nextPreference,
        },
      }).catch(() => undefined);
    }
  };

  useEffect(() => {
    return store.subscribe(() => {
      const auth = store.getState().auth;
      setAuthState((currentAuthState) => {
        if (
          currentAuthState.accessToken === auth.accessToken &&
          currentAuthState.triedAuthenticateFromLocalStorage === auth.triedAuthenticateFromLocalStorage
        ) {
          return currentAuthState;
        }

        return {
          accessToken: auth.accessToken,
          triedAuthenticateFromLocalStorage: auth.triedAuthenticateFromLocalStorage,
        };
      });
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateSystemMode = (event: MediaQueryList | MediaQueryListEvent) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };

    updateSystemMode(mediaQuery);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateSystemMode);
      return () => mediaQuery.removeEventListener('change', updateSystemMode);
    }

    mediaQuery.addListener(updateSystemMode);
    return () => mediaQuery.removeListener(updateSystemMode);
  }, []);

  useEffect(() => {
    if (!authState.triedAuthenticateFromLocalStorage) {
      return;
    }

    if (!authState.accessToken) {
      lastLoadedAccessTokenRef.current = null;
      return;
    }

    if (lastLoadedAccessTokenRef.current === authState.accessToken) {
      return;
    }

    lastLoadedAccessTokenRef.current = authState.accessToken;

    let ignoreResponse = false;

    void getCurrentUserPreferences()
      .then((userPreferences) => {
        if (ignoreResponse) {
          return;
        }

        const serverPreference = getThemePreferenceFromUserPreferences(userPreferences ?? {});
        if (serverPreference) {
          applyPreference(serverPreference, false);
          return;
        }

        const localPreference = getStoredColorModePreference();
        if (hasStoredColorModePreference() && localPreference) {
          applyPreference(localPreference, true);
        }
      })
      .catch(() => undefined);

    return () => {
      ignoreResponse = true;
    };
  }, [authState.accessToken, authState.triedAuthenticateFromLocalStorage]);

  const mode = preference === 'system' ? systemMode : preference;
  const colorMode = {
    toggleColorMode: () => {
      applyPreference(mode === 'light' ? 'dark' : 'light');
    },
    mode,
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={colorMode.mode === 'light' ? lightTheme : darkTheme}>{children}</ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);
